import { useCallback, useEffect, useState } from 'react';
import { useReenrollUserMutation } from 'services/enrollmentService';
import { useLazyGetUserQuery } from 'services/authService';
import { useAppDispatch } from 'store/store';
import { setIsUserDoneIneligible, setUser } from 'features/auth/authSlice';
import { useCobrandedPath, useNavigate } from 'features/cobrand/hooks/useCobrandUtils';

type EnrollmentStatus = 'idle' | 'processing' | 'complete' | 'error';

export interface UseEnrollmentResult {
  isProcessing: boolean;
  error: string | null;
}

/**
 * useEnrollment - Handles enrollment processing
 * @param enrollmentId - The enrollment code from the deep link URL
 * @returns Processing state and error information
 */
export const useEnrollment = (enrollmentId: string | null): UseEnrollmentResult => {
  const [status, setStatus] = useState<EnrollmentStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const [reenrollUser] = useReenrollUserMutation();
  const [refreshUser] = useLazyGetUserQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const homePagePath = useCobrandedPath('/home');

  const processEnrollment = useCallback(
    async (id: string) => {
      setStatus('processing');
      setError(null);
      try {
        // Step 1: Try to call reenroll (may succeed or fail)
        try {
          await reenrollUser(id).unwrap();
        } catch (reenrollError: any) {
          // Continue to me endpoint regardless of error type
        }

        const { data: userData, error: refreshError } = await refreshUser();
        if (refreshError || !userData) {
          throw new Error('Failed to fetch user data after enrollment');
        }
        const { eligible, wellframe_membership_state: membershipState } = userData;
        const showDialog =
          membershipState === 'terminated' ||
          membershipState === 'done' ||
          (membershipState === 'enrolled' && !eligible);

        dispatch(setUser(userData));
        dispatch(setIsUserDoneIneligible(showDialog));
        navigate(homePagePath, { replace: true });
        setStatus('complete');
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred while processing your enrollment';
        setError(errorMessage);
        setStatus('error');
      }
    },
    [reenrollUser, refreshUser, dispatch, navigate, homePagePath],
  );
  useEffect(() => {
    if (enrollmentId && status === 'idle') {
      processEnrollment(enrollmentId);
    }
  }, [enrollmentId, status, processEnrollment]);

  return { isProcessing: status === 'processing', error };
};
